import { ReactNode } from "react";

// eslint-disable-next-line no-restricted-imports
import { css, keyframes } from "@emotion/react";

import { P300 } from "@atlaskit/theme/colors";
import { layers } from "@atlaskit/theme/constants";
import { token } from "@atlaskit/tokens";

type BaseProps = React.HTMLAttributes<HTMLDivElement> & {
    bgColor?: string;
    radius?: number;
    className?: string;
    children?: ReactNode;
};

type TargetProps = Omit<BaseProps, "css"> & {
    pulse?: boolean;
    pulseSize?: number;
    speed?: "slow" | "fast";
};

// IE11 and Edge: z-index needed because fixed position calculates z-index relative
// to body instead of nearest stacking context (Portal in our case).
const baseStyles = css`
    z-index: ${layers.spotlight() + 1};
`;

const Base = ({ children, bgColor, radius, style, ...props }: BaseProps) => (
    <div
        css={baseStyles}
        style={
            {
                ...style,
                backgroundColor: bgColor,
                borderRadius: radius ? `${radius}px` : undefined
            } as React.CSSProperties
        }
        {...props}
    >
        {children}
    </div>
);

export const Pulse = ({
    children,
    pulse = true,
    pulseSize = 15,
    speed = "fast",
    ...props
}: TargetProps) => {
    // NOTE:
    // Pulse color "rgb(101, 84, 192)" derived from "colors.P300"
    const baseShadow = `0 0 0 2px ${token("color.border.discovery", P300)}`;
    const easing = "cubic-bezier(0.55, 0.055, 0.675, 0.19)";
    const pulseKeyframes = keyframes({
        "0%, 33%": {
            boxShadow: `${baseShadow}, 0 0 0 ${token(
                "color.border.discovery",
                "rgba(101, 84, 192, 1)"
            )}`
        },
        "66%, 100%": {
            boxShadow: `${baseShadow}, 0 0 0 ${pulseSize}px rgba(101, 84, 192, 0.01)`
        }
    });

    const animationStyles = css`
        animation: ${pulseKeyframes} ${speed === "slow" ? "4000ms" : "2000ms"}
            ${easing} infinite;
    `;

    return (
        <Base {...props} css={[pulse && animationStyles]}>
            {children}
        </Base>
    );
};
